import React from "react";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Button, FormControl, Grid, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { PricingVector } from "client/api-v2";
import { FormSelect } from "components/form/FormSelect";
import type { CreateRateCardFormValues } from "./createRateCardForm";
import { validateDecimalString } from "./createRateCardForm";

const allVectors = Object.values(PricingVector);

export function UnitPriceRows() {
  const { control } = useFormContext<CreateRateCardFormValues>();
  // The remove button is disabled on the last remaining row, so "at least one unit
  // price" is enforced structurally rather than as a validation message.
  const { fields, append, remove } = useFieldArray({ control, name: "unitPrices" });
  const unitPrices = useWatch({ control, name: "unitPrices" }) ?? [];

  const usedVectors = unitPrices.map((p) => p.vector);
  const firstUnusedVector = allVectors.find((v) => !usedVectors.includes(v));

  return (
    <>
      <Grid size={12}>
        <Typography variant="subtitle2">Unit prices</Typography>
      </Grid>
      {fields.map((field, index) => {
        // Vectors already priced by other rows are omitted so a duplicate is
        // unrepresentable, rather than a validation error after the fact.
        const vectorsForRow = allVectors.filter((v) => v === unitPrices[index]?.vector || !usedVectors.includes(v));
        return (
          <React.Fragment key={field.id}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <FormControl fullWidth>
                <FormSelect
                  name={`unitPrices.${index}.vector`}
                  label="Pricing vector"
                  labelId={`unit-price-vector-${index}`}
                  selectItems={vectorsForRow.map((v) => [v, v])}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 10, sm: 5 }}>
              <Controller
                name={`unitPrices.${index}.listPricePerUnit`}
                control={control}
                rules={{
                  validate: (value) =>
                    validateDecimalString(value, {
                      required: "Unit prices are required",
                      mustBeNumber: "Unit prices must be a number",
                      negative: "Unit prices cannot be negative",
                      maxDecimals: 2,
                      overPrecise: "Unit prices can have at most 2 decimal places",
                    }),
                }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    fullWidth
                    label="Price per unit"
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                      htmlInput: { inputMode: "decimal" },
                    }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            {/* mt aligns the button with the input's centre even when helper text
                stretches the cell; a tooltip on a disabled button needs the span. */}
            <Grid size={{ xs: 2, sm: 2 }} sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
              <Tooltip title={fields.length === 1 ? "A rate card needs at least one unit price" : "Remove unit price"}>
                <span>
                  <IconButton
                    aria-label={`Remove unit price ${index + 1}`}
                    onClick={() => remove(index)}
                    disabled={fields.length === 1}
                    size="small"
                    sx={{ color: "text.secondary" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </React.Fragment>
        );
      })}
      <Grid size={12}>
        <Button
          startIcon={<AddIcon />}
          onClick={() => firstUnusedVector && append({ vector: firstUnusedVector, listPricePerUnit: "" })}
          disabled={firstUnusedVector === undefined}
        >
          Add unit price
        </Button>
      </Grid>
    </>
  );
}
